import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  Modal,
  Button,
  Table,
  Loader,
  Message,
  Icon,
  Confirm,
  Label,
} from 'semantic-ui-react';
import { injectIntl } from 'react-intl';
import {
  getRohstoffHistory,
  getRohstoffVersion,
  restoreRohstoffVersion,
  resetRohstoffHistory,
  getRohstoffByID,
  getRohstoffkategorien,
} from '../../../actions';
import {
  versionHistoryMessages as messages,
  operationLabels,
  formatDateTime,
  getChangedFields,
  filterChanges,
  formatEnumValue,
} from '../versionHistoryUtils';

const RohstoffVersionHistory = ({
  rohstoffId,
  intl,
  history,
  historyLoading,
  historyError,
  version,
  versionLoading,
  restore,
  restoreLoading,
  getRohstoffHistory,
  getRohstoffVersion,
  restoreRohstoffVersion,
  resetRohstoffHistory,
  getRohstoffByID,
  getRohstoffkategorien,
  rohstoffkategorien,
  trigger,
  canRestore,
}) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  // Create a lookup map for kategorie names
  const kategorienMap = React.useMemo(() => {
    const map = {};
    if (rohstoffkategorien?.rohstoffkategorien) {
      rohstoffkategorien.rohstoffkategorien.forEach((k) => {
        map[k.id] = k.name;
      });
    }
    return map;
  }, [rohstoffkategorien]);

  useEffect(() => {
    if (open && rohstoffId) {
      getRohstoffHistory(rohstoffId);
      getRohstoffkategorien({ getAll: true });
    }
  }, [open, rohstoffId, getRohstoffHistory, getRohstoffkategorien]);

  useEffect(() => {
    if (restore.success) {
      // Refresh history after successful restore
      getRohstoffHistory(rohstoffId);
      // Refresh the rohstoff data
      getRohstoffByID(rohstoffId);
      setConfirmOpen(false);
    }
  }, [restore.success, rohstoffId, getRohstoffHistory, getRohstoffByID]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDetailsOpen(false);
    setSelectedDetails(null);
    resetRohstoffHistory();
  };

  const handleRestoreClick = (versionNumber) => {
    setSelectedVersion(versionNumber);
    setConfirmOpen(true);
  };

  const handleRestoreConfirm = () => {
    if (selectedVersion) {
      restoreRohstoffVersion(rohstoffId, selectedVersion);
    }
  };

  const handleViewDetails = (entry) => {
    setSelectedDetails(entry);
    setDetailsOpen(true);
  };

  // Helper to format kategorien for display
  const formatKategorien = (kategorienList) => {
    if (!kategorienList || !Array.isArray(kategorienList)) return '-';
    if (kategorienList.length === 0) return 'Keine Kategorien';
    return kategorienList
      .map((id) => kategorienMap[id] || `ID: ${id}`)
      .join(', ');
  };

  // Helper to render value based on field type
  const renderValue = (field, value) => {
    if (field === 'kategorien') {
      return formatKategorien(value);
    }
    const enumLabel = formatEnumValue(field, value);
    if (enumLabel !== null) {
      return enumLabel;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderChanges = (changes) => {
    if (!changes) return null;

    const { filteredChanged, filteredAdded, filteredRemoved, hasChanges } =
      filterChanges(changes);

    if (!hasChanges) {
      return <p>Keine Details verfügbar</p>;
    }

    return (
      <div className="changes-container">
        {Object.keys(filteredChanged).length > 0 && (
          <div className="changes-section">
            <h4>Geänderte Felder:</h4>
            <Table compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Feld</Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage(messages.oldValue)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage(messages.newValue)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {Object.entries(filteredChanged).map(([field, value]) => (
                  <Table.Row key={field}>
                    <Table.Cell>
                      <strong>{field}</strong>
                    </Table.Cell>
                    <Table.Cell className="old-value">
                      {renderValue(field, value.old_value)}
                    </Table.Cell>
                    <Table.Cell className="new-value">
                      {renderValue(field, value.new_value)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        {Object.keys(filteredAdded).length > 0 && (
          <div className="changes-section">
            <h4>Hinzugefügte Felder:</h4>
            <Table compact size="small">
              <Table.Body>
                {Object.entries(filteredAdded).map(([field, value]) => (
                  <Table.Row key={field} positive>
                    <Table.Cell>
                      <strong>{field}</strong>
                    </Table.Cell>
                    <Table.Cell>{renderValue(field, value)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        {Object.keys(filteredRemoved).length > 0 && (
          <div className="changes-section">
            <h4>Entfernte Felder:</h4>
            <Table compact size="small">
              <Table.Body>
                {Object.entries(filteredRemoved).map(([field, value]) => (
                  <Table.Row key={field} negative>
                    <Table.Cell>
                      <strong>{field}</strong>
                    </Table.Cell>
                    <Table.Cell>{renderValue(field, value)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {trigger ? (
        <span
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpen();
            }
          }}
          role="button"
          tabIndex={0}
        >
          {trigger}
        </span>
      ) : (
        <Button onClick={handleOpen} className="version-history-button">
          {intl.formatMessage(messages.versionHistory)}
        </Button>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        size="large"
        className="version-history-modal"
      >
        <Modal.Header>
          {intl.formatMessage(messages.versionHistory)}
        </Modal.Header>
        <Modal.Content scrolling>
          {historyLoading ? (
            <Loader active inline="centered">
              Lade Versionshistorie...
            </Loader>
          ) : historyError ? (
            <Message negative>
              <Message.Header>Fehler</Message.Header>
              <p>
                {historyError.message ||
                  'Fehler beim Laden der Versionshistorie'}
              </p>
            </Message>
          ) : restore.success ? (
            <Message positive>
              <Message.Header>
                {intl.formatMessage(messages.restoreSuccessRohstoff)}
              </Message.Header>
            </Message>
          ) : history.history && history.history.length > 0 ? (
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={1}>
                    {intl.formatMessage(messages.version)}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2}>
                    {intl.formatMessage(messages.operation)}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={2}>
                    {intl.formatMessage(messages.changedBy)}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={3}>
                    {intl.formatMessage(messages.changedAt)}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={4}>
                    {intl.formatMessage(messages.description)}
                  </Table.HeaderCell>
                  <Table.HeaderCell width={4}>
                    {intl.formatMessage(messages.actions)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {history.history.map((entry, index) => {
                  const opLabel = operationLabels[entry.operation_type];
                  const isLatest = index === 0;

                  return (
                    <Table.Row key={entry.history_id}>
                      <Table.Cell>
                        <strong>V{entry.version_number}</strong>
                        {isLatest && (
                          <Label size="mini" color="green" horizontal>
                            Aktuell
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Label color={opLabel?.color || 'grey'} size="small">
                          {opLabel
                            ? intl.formatMessage(opLabel.message)
                            : entry.operation_type}
                        </Label>
                        {entry.restored_from_version && (
                          <span className="restored-from">
                            {' '}
                            (von V{entry.restored_from_version})
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {entry.changed_by_name ||
                          `Benutzer ${entry.changed_by}`}
                      </Table.Cell>
                      <Table.Cell>
                        {formatDateTime(entry.changed_at)}
                      </Table.Cell>
                      <Table.Cell>
                        {entry.change_description && (
                          <div>{entry.change_description}</div>
                        )}
                        {getChangedFields(entry.changes_made).length > 0 && (
                          <div className="changed-fields">
                            {getChangedFields(entry.changes_made).join(', ')}
                          </div>
                        )}
                        {!entry.change_description &&
                          getChangedFields(entry.changes_made).length === 0 &&
                          '-'}
                      </Table.Cell>
                      <Table.Cell>
                        <Button.Group size="small">
                          <Button
                            icon
                            onClick={() => handleViewDetails(entry)}
                            title={intl.formatMessage(messages.viewDetails)}
                          >
                            <Icon name="eye" />
                          </Button>
                          {canRestore && !isLatest && (
                            <Button
                              icon
                              color="blue"
                              onClick={() =>
                                handleRestoreClick(entry.version_number)
                              }
                              title={intl.formatMessage(messages.restore)}
                              loading={restoreLoading}
                            >
                              <Icon name="undo" />
                            </Button>
                          )}
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          ) : (
            <Message info>
              <Message.Header>
                {intl.formatMessage(messages.noHistory)}
              </Message.Header>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleClose}>
            {intl.formatMessage(messages.close)}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Details Modal */}
      <Modal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        size="small"
        className="version-history-modal"
      >
        <Modal.Header>
          Version {selectedDetails?.version_number} - Einzelheiten
        </Modal.Header>
        <Modal.Content scrolling>
          {selectedDetails && (
            <div className="version-details">
              <div className="version-meta">
                <p>
                  <strong>Aktion:</strong>{' '}
                  <Label
                    color={
                      operationLabels[selectedDetails.operation_type]?.color ||
                      'grey'
                    }
                  >
                    {operationLabels[selectedDetails.operation_type]
                      ? intl.formatMessage(
                          operationLabels[selectedDetails.operation_type]
                            .message,
                        )
                      : selectedDetails.operation_type}
                  </Label>
                </p>
                <p>
                  <strong>Geändert von:</strong>{' '}
                  {selectedDetails.changed_by_name ||
                    `Benutzer ${selectedDetails.changed_by}`}
                </p>
                <p>
                  <strong>Geändert am:</strong>{' '}
                  {formatDateTime(selectedDetails.changed_at)}
                </p>
                {selectedDetails.change_description && (
                  <p>
                    <strong>Beschreibung:</strong>{' '}
                    {selectedDetails.change_description}
                  </p>
                )}
              </div>

              {selectedDetails.changes_made && (
                <div className="version-changes">
                  <h3>{intl.formatMessage(messages.changes)}</h3>
                  {renderChanges(selectedDetails.changes_made)}
                </div>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDetailsOpen(false)}>
            {intl.formatMessage(messages.close)}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Restore Confirmation */}
      <Confirm
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleRestoreConfirm}
        header="Wiederherstellen bestätigen"
        content={`Möchten Sie wirklich zu Version ${selectedVersion} zurückkehren? Diese Aktion erstellt eine neue Version mit dem Zustand der ausgewählten Version.`}
        cancelButton="Abbrechen"
        confirmButton="Wiederherstellen"
      />
    </>
  );
};

RohstoffVersionHistory.propTypes = {
  rohstoffId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  intl: PropTypes.object.isRequired,
  history: PropTypes.object,
  historyLoading: PropTypes.bool,
  historyError: PropTypes.object,
  version: PropTypes.object,
  versionLoading: PropTypes.bool,
  restore: PropTypes.object,
  restoreLoading: PropTypes.bool,
  getRohstoffHistory: PropTypes.func.isRequired,
  getRohstoffVersion: PropTypes.func.isRequired,
  restoreRohstoffVersion: PropTypes.func.isRequired,
  resetRohstoffHistory: PropTypes.func.isRequired,
  getRohstoffByID: PropTypes.func.isRequired,
  getRohstoffkategorien: PropTypes.func.isRequired,
  rohstoffkategorien: PropTypes.object,
  trigger: PropTypes.node,
  canRestore: PropTypes.bool,
};

RohstoffVersionHistory.defaultProps = {
  history: { history: [], total: 0 },
  historyLoading: false,
  historyError: null,
  version: null,
  versionLoading: false,
  restore: { success: false, loading: false },
  restoreLoading: false,
  rohstoffkategorien: { rohstoffkategorien: [] },
  canRestore: false,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      history: state.rohstoffHistory.data,
      historyLoading: state.rohstoffHistory.loading,
      historyError: state.rohstoffHistory.error,
      version: state.rohstoffVersion.data,
      versionLoading: state.rohstoffVersion.loading,
      restore: state.rohstoffRestore,
      restoreLoading: state.rohstoffRestore.loading,
      rohstoffkategorien: state.rohstoffkategorien.data,
    }),
    {
      getRohstoffHistory,
      getRohstoffVersion,
      restoreRohstoffVersion,
      resetRohstoffHistory,
      getRohstoffByID,
      getRohstoffkategorien,
    },
  ),
)(RohstoffVersionHistory);
